/* eslint-disable max-len */
import OlProjection from 'ol/proj/Projection';
import OlStyle from 'ol/style/Style';
import OlStroke from 'ol/style/Stroke';
import OlFill from 'ol/style/Fill';
import { Circle as CircleStyle, Fill, Stroke } from 'ol/style';
import OlMultiPoint from 'ol/geom/MultiPoint';
import i18n from '../i18n';

// Projected coordinate system for Netherlands
export const PROJECTION_CODE = 'EPSG:28992';
export const PROJ4_PROJECTION_DEFENITION = '+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 +x_0=155000 +y_0=463000+ellps=bessel +towgs84=565.417,50.3319,465.552,-0.398957,0.343988,-1.8774,4.0725 +units=m +no_defs';

// Coordinate reference system extent for Amersfoort RD, see: https://epsg.io/28992
export const PROJECTION_AMERSFOORT_RD_BOUNDS = {
    minX: 646.36,
    maxX: 308975.28,
    minY: 276050.82,
    maxY: 636456.31
};

export const NEDGLOBE_MAP_PROJECTION_EXTENT = [-285401.92, 22598.08, 595401.92, 903401.92];

export const NEDGLOBE_MAP_PROJECTION = new OlProjection({ code: PROJECTION_CODE, extent: NEDGLOBE_MAP_PROJECTION_EXTENT, units: 'm' });
export const NEDGLOBE_MAP_RESOLUTION = [3440.64, 1720.32, 860.16, 430.08, 215.04, 107.52, 53.76, 26.88, 13.44, 6.72, 3.36, 1.68, 0.84, 0.42, 0.21, 0.105, 0.0525, 0.02625];
export const NEDGLOBE_MAP_SCALES = [12288000, 6144000, 3072000, 1536000, 768000, 384000, 192000, 96000, 48000, 24000, 12000, 6000, 3000, 1500, 750, 375, 187.5, 93.75];

export const DEFAULT_MIN_ZOOM_RESOLUTION = NEDGLOBE_MAP_RESOLUTION[NEDGLOBE_MAP_RESOLUTION.length - 4];
export const DEFAULT_WEB_CLUSTER_MIN_ZOOM_RESOLUTION = 0.01;

export const ZOOM_LEVEL_SCALE_750 = 11;

export const INCHES_PER_METER = 39.37;
export const CM_PER_INCH = 2.54;
export const DEFAULT_DOTS_PER_INCH = 90.71428571428571;
export const DOTS_PER_INCH = 96;
export const POINT_TOLERANCE_DIAMETER_IN_MM = 8;
export const RAD_PER_DEGREE = Math.PI / 180;

export const GEOLOCATION_POSITION_TIMEOUT = 5000;

export const STYLE_TYPE = {
    Empty: { typename: 'Empty' },
    Default: { value: 0, typename: 'Default', zIndex: 800 },
    Selection: { value: 1, typename: 'Selection', zIndex: 801 },
    Selected: { value: 2, typename: 'Selected', zIndex: 802 }
};

export const NEDGLOBE_VIEWER = {
    customLayersIndexes: {
        start: 100000,
        default: 8000
    },
    userLayers: {
        endIndex: 7999,
        themesStartIndex: 3000,
        feedbacksStartIndex: 2000,
        measurementsStartIndex: 1000
    },
    mapLayerZIndexes: {
        feedback: 700,
        theme: 300,
        coveringTheme: 1,
        base: 0
    },
    customLayers: {
        default: 'selections',
        printable: {
            selectionArea: 'selectionArea',
            organizationHighlightBorder: 'organizationHighlightBorder',
            selections: 'selections',
            selection: 'selection',
            search: 'search',
        },
        displayOnly: {
            drawing: 'drawing',
            marker: 'marker',
            markerGPS: 'markerGPS'
        }
    },
    styleDisplayTypes: {
        default: 'Default',
        selected: 'Selected',
        selection: 'Selection'
    },
    styleOptionNames: {
        fillColor: 'fillColor',
        strokeColor: 'strokeColor',
        pointSize: 'pointSize',
        strokeWidth: 'strokeWidth',
        textAttribute: 'textAttribute',
        textFillColor: 'textFillColor',
        textStrokeColor: 'textStrokeColor',
        textStrokeWidth: 'textStrokeWidth',
        textFont: 'textFont',
        textFontSize: 'textFontSize',
        textMaxResolution: 'textMaxResolution',
        rotationAttribute: 'rotationAttribute',
        imageSvg: 'imageSvg',
        rotationSvg: 'rotationSvg',
        sizeSvg: 'sizeSvg',
        icon: 'icon',
        iconFill: 'iconFill',
        iconFillOpacity: 'iconFillOpacity',
        iconSize: 'iconSize',
        iconsizeunits: 'iconSizeUnits',
        iconStroke: 'iconStroke',
        iconStrokeOpacity: 'iconStrokeOpacity',
        iconStrokeWidth: 'iconStrokeWidth'
    },
    styledObjectTypes: {
        Style: 'Style',
        CircleStyle: 'Circle',
        Icon: 'Icon',
        Fill: 'Fill',
        Stroke: 'Stroke',
        Text: 'Text',
        Point: 'Point'
    }
};

export const DEFAULT_FEATURE_STYLE_SETTINGS_NORMAL = {
    'strokeColorDefault': 'rgba(0, 0, 0, 1)',
    'strokeWidthDefault': 1,
    'fillColorDefault': 'rgba(0, 0, 255, 0.25)',
    'pointSizeDefault': 5,
    'iconDefault': null,
    'textFontDefault': 'Arial, Helvetica, sans-serif',
    'textFontSizeDefault': 12,
    'textFillColorDefault': 'rgba(0, 0, 0, 1)',
    'textStrokeColorDefault': 'rgba(255, 255, 255, 1)',
    'textStrokeWidthDefault': 1,
    'textAttributeDefault': null,
    'pointImage': null,
    'customStylingOptions': null
};

const DEFAULT_FEATURE_STYLE_SETTINGS_SELECTED = {
    'strokeColorSelection': 'rgba(0, 0, 0, 1)',
    'strokeWidthSelection': 2,
    'fillColorSelection': 'rgba(255, 0, 0, 0.65)',
    'pointSizeSelection': 5,
    'iconSelection': null,
    'strokeColorSelected': 'rgba(0, 0, 0, 1)',
    'strokeWidthSelected': 2,
    'fillColorSelected': 'rgba(255, 255, 0, 0.65)',
    'pointSizeSelected': 5,
    'iconSelected': null,
};

export const STYLE_CLASSIFICATION_TYPES = {
    NOMINAL: 'N',
    RANGES: 'R'
};

export const NUMERIC_DATATYPES = [
    'int',
    'negativeInteger',
    'long',
    'short',
    'byte',
    'integer',
    'float',
    'double',
    'decimal',
    'number'
];

export const DEFAULT_FEATURE_STYLE_SETTINGS = {
    ...DEFAULT_FEATURE_STYLE_SETTINGS_NORMAL,
    ...DEFAULT_FEATURE_STYLE_SETTINGS_SELECTED
};

export const DEFAULT_DRAW_STYLE = (radius = 7) => new OlStyle({
    stroke: new OlStroke({
        color: '#007ac9',
        width: 3
    }),
    fill: new OlFill({ color: '#007ac93d' }),
    image: new CircleStyle({
        radius,
        fill: new Fill({ color: '#007ac93d', }),
        stroke: new OlStroke({
            color: '#007ac9',
            width: 3
        }),
    }),
});

export const DEFAULT_SELECTION_STYLE = DEFAULT_DRAW_STYLE();

export const DEFAULT_MODIFY_STYLE = (radius = 7) => new OlStyle({
    stroke: new OlStroke({
        color: '#c79500',
        width: 3
    }),
    fill: new OlFill({ color: '#c795003d' }),
    image: new CircleStyle({
        radius,
        fill: new Fill({ color: '#c795003d', }),
        stroke: new OlStroke({
            color: '#c79500',
            width: 3
        }),
    }),
});

export const DEFAULTPOLYGON_DRAW_STYLE = new OlStyle({
    image: new CircleStyle({
        radius: 6,
        fill: new OlFill({ color: 'rgba(255,255,255,1)' }),
        stroke: new OlStroke({ color: 'rgba(0,153,255,1)', width: 2 })
    }),
    geometry: function (feature) {
        const coordinates = feature.getGeometry().getCoordinates()[0];
        return new OlMultiPoint(coordinates);
    }
});

export const DEFAULTLINE_DRAW_STYLE = new OlStyle({
    image: new CircleStyle({
        radius: 6,
        fill: new OlFill({ color: 'rgba(255,255,255,1)' }),
        stroke: new OlStroke({ color: 'rgba(0,153,255,1)', width: 2 })
    }),
    geometry: function (feature) {
        const coordinates = feature.getGeometry().getCoordinates();
        return new OlMultiPoint(coordinates);
    }
});

// minimap styles
export const PRIMARY_DRAW_STYLE = new OlStyle({
    stroke: new OlStroke({
        color: 'rgb(255,0,0,1)',
        width: 2
    }),
    fill: new OlFill({ color: 'rgba(255, 0, 0, 0.2)' }),
    image: new CircleStyle({
        radius: 4,
        fill: new Fill({ color: 'rgb(255,0,0,0.2)' }),
        stroke: new Stroke({ color: 'rgb(255,0,0,1)', width: 1 }),
    })
});

export const PRIMARY_SELECTED_DRAW_STYLE = new OlStyle({
    stroke: new OlStroke({
        color: 'rgb(255,0,0,1)',
        width: 2
    }),
    fill: new OlFill({ color: 'rgba(255, 0, 0, 1)' }),
    image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: 'rgb(255,0,0,1)' }),
        stroke: new Stroke({ color: 'rgb(255,0,0,1)', width: 1 }),
    })
});

export const PRIMARY_HIGHLIGHTED_DRAW_STYLE = new OlStyle({
    stroke: new OlStroke({
        color: 'rgba(0, 0, 0, 1)',
        width: 2
    }),
    fill: new OlFill({ color: 'rgba(255, 255, 0, 0.65)' }),
    image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: 'rgb(255, 255, 0, 0.65)' }),
        stroke: new Stroke({ color: 'rgb(0, 0, 0, 1)', width: 1 }),
    })
});

export const SECONDARY_DRAW_STYLE = new OlStyle({
    stroke: new OlStroke({
        color: 'rgba(0, 122, 201, 1)',
        width: 1
    }),
    fill: new OlFill({ color: 'rgba(0, 122, 201, 0.2)' })
});

// Layer style export const
export const LAYER_STYLE = {
    stroke_width: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
    point_radius: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25],
    font_sizes: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25],
    font_faces: ['Arial, Helvetica, sans-serif', 'Arial Black, Gadget, sans-serif',
        'Comic Sans MS, cursive, sans-serif', 'Impact, Charcoal, sans-serif',
        'Lucida Sans Unicode, Lucida Grande, sans-serif', 'Tahoma, Geneva, sans-serif',
        'Trebuchet MS, Helvetica, sans-serif', 'Verdana, Geneva, sans-serif']
};

export const OWS_TYPE = {
    'WFS': 'WFS',
    'WMS': 'WMS',
    'WMTS': 'WMTS',
    'OGCT': 'OGCT', // OGC API Tile layer
    'OGCF': 'OGCF'  // OGC API Feature layer
};

export const NG_LAYER_TYPES = {
    'BASE': 'base',
    'CUSTOM': 'custom',
    'THEME': 'theme',
    'FEEDBACK': 'feedback'
};

export const INTERACTION_TYPE = {
    Point: 'Point',
    Circle: 'Circle',
    Polygon: 'Polygon',
    LineString: 'LineString',
};

export const DRAW_MODUS = {
    Draw: 'draw',
    Modify: 'modify',
};

// timeouts for interactions
export const MAP_INTERACTION_TIMEOUTS = {
    longHold: 750,
    shortHold: 10,
    debounceDefaultWaitMs: 250,
    doubleClickDelayMS: 260,
};

export const GPS_MARKER_TYPE = {
    Default: 0,
    Rose: 1,
    RoseSelected: 2,
    Direction: 3,
};

// list of margings to add to focus items on the map
export const MAP_MARGINS = {
    featuresImported: 25,
    featuresZoom: 75,
    clusterSelection: 200
};

export const DEFAULT_CIRCLE_SELECTION_RADIUS = 50;

export const BOUNDING_BOX_MARGIN = 10;

export const MAX_RESULTS_RELATION = 1000;

export const LABEL_SEPARATOR_OPTIONS = [
    {
        value: ' ',
        text: i18n.t('separatorSpace')
    },
    {
        value: ',',
        text: ','
    },
    {
        value: '.',
        text: '.'
    },
    {
        value: '-',
        text: '-'
    },
    {
        value: ';',
        text: ';'
    },
    {
        value: '|',
        text: '|'
    },
    {
        value: '/',
        text: '/'
    },
    {
        value: 'lf',
        text: i18n.t('separatorLf')
    }
];

export const THEMAGROUP_OVERIG = {
    NR: 9999,
    NAME: 'Overig'
};
